import { Component, OnDestroy, OnInit } from '@angular/core';
import { LogService } from '@nts/std/utility';
import { BaseAggregateComponent } from '../base-aggregate/base-aggregate.component';
import { ExpandableComponent } from '../shared/expandable/expandable.component';
import { NgFor, NgIf } from '@angular/common';
import { SpacerBoxComponent } from '../controls/spacer-box/spacer-box.component';
import { LabelledTextBoxComponent } from '../controls/labelled-text-box/labelled-text-box.component';
import { DecodeTextBoxComponent } from '../controls/decode-text-box/decode-text-box.component';
import { ExternalListComponent } from '../controls/external-list/external-list.component';
import { CustomFieldContainerComponent } from '../controls/custom-field-container/custom-field-container.component';
import { GridComponent } from '../../../../components/controls/grid/grid.component';
import type { ColumnInfoCollectionInterface, CustomFieldMetaDataInterface, ExternalLayoutMetaDataInterface, ExternalViewModelInterface, FieldMetaDataInterface, GridFieldInterface, GridMetaDataInterface, LayoutMetaDataInterface, PanelMetaDataInterface, PropertyViewModelInterface, RootViewModelInterface, SimpleFieldMetaDataInterface, ViewModelInterface } from '@nts/std/interfaces';
import { FieldTypes, PanelState } from '@nts/std/types';
import { MetaDataUtils } from '../../meta-data/meta-data-utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fromEvent } from 'rxjs';
import { resolveResponsiveFieldMetaData } from '../../view-models/responsive-configuration.utility';

@UntilDestroy()
@Component({
  selector: 'nts-dynamic-presentation-component',
  templateUrl: 'dynamic-presentation.component.html',
  styleUrls: ['./dynamic-presentation.component.scss'],
  standalone: true,
  imports: [
    ExpandableComponent,
    NgIf,
    SpacerBoxComponent,
    LabelledTextBoxComponent,
    GridComponent,
    DecodeTextBoxComponent,
    ExternalListComponent,
    CustomFieldContainerComponent,
    NgFor,
  ]
})
export class DynamicPresentationComponent extends BaseAggregateComponent<RootViewModelInterface> implements OnInit, OnDestroy {


  fieldType = FieldTypes;
  panelState = PanelState;
  private viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

  get layoutMetaData(): LayoutMetaDataInterface {
    return this.orchestratorViewModel.layoutMetaData;
  }

  private enableCache = false;
  private promotedFieldsCache = {};
  private propertyViewModelCache = {};
  private collectionViewModelColumnCache = {};

  override ngOnInit() {
    LogService.log('DynamicPresentation - INIT');
    this.orchestratorViewModel.rootViewModelChanged.pipe(untilDestroyed(this)).subscribe(() => {
      this.promotedFieldsCache = {};
      this.propertyViewModelCache = {};
    });

    if (typeof window !== 'undefined') {
      fromEvent(window, 'resize')
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.viewportWidth = window.innerWidth;
        });
    }
  }

  ngOnDestroy(): void {
    this.promotedFieldsCache = {};
    this.propertyViewModelCache = {};
    this.collectionViewModelColumnCache = {};
  }

  getCustomFieldMetaDataFromFieldMetaData(field: FieldMetaDataInterface) {
    return field as CustomFieldMetaDataInterface;
  }

  getSimpleFieldMetaDataFromFieldMetaData(field: FieldMetaDataInterface) {
    return field as SimpleFieldMetaDataInterface;
  }

  getGridMetaDataFromFieldMetaData(field: FieldMetaDataInterface) {
    return field as GridMetaDataInterface;
  }

  getExternalMetaDataFromFieldMetaData(field: FieldMetaDataInterface) {
    return field as ExternalLayoutMetaDataInterface;
  }

  getResponsiveFieldMetaData<TField extends FieldMetaDataInterface>(field: TField): TField {
    return resolveResponsiveFieldMetaData(this.viewportWidth, field);
  }

  getResponsiveFields(panel: PanelMetaDataInterface): FieldMetaDataInterface[] {
    return panel.sortedFields
      .map((field) => this.getResponsiveFieldMetaData(field))
      .sort((a, b) => a.position - b.position);
  }

  getPromotedFields(panel: PanelMetaDataInterface, panelIndex: number) {
    if (this.promotedFieldsCache[panelIndex] && this.enableCache) {
      return this.promotedFieldsCache[panelIndex];
    }
    return panel.promotedFields.map((fieldFullPath: string) => {
      const correctedPath = this.fixSelectedItemInPath(fieldFullPath);
      const result = correctedPath.split('.').reduce((acc, current) => acc[current], this.orchestratorViewModel.rootViewModel);
      this.promotedFieldsCache[panelIndex] = result;
      return result;
    });
  }

  getPropertyFromFieldMetaData(field: FieldMetaDataInterface): PropertyViewModelInterface | ExternalViewModelInterface {
    const cacheKey = field.path?.length > 0 ? field.path + '.' + field.name : field.name;
    if (this.propertyViewModelCache[cacheKey] !== undefined && this.enableCache) {
      return this.propertyViewModelCache[cacheKey];
    }

    let result: PropertyViewModelInterface | ExternalViewModelInterface;
    let vm: ViewModelInterface = null;

    if (field.path?.length > 0) {
      vm = this.getResolvedPropertyPath(field);
    } else {
      vm = this.orchestratorViewModel.rootViewModel as ViewModelInterface;
    }

    if (vm) {

      if (field.isExtension) {
        result = vm.extension.propertyViewModels.get(MetaDataUtils.toCamelCase(field.name)) as PropertyViewModelInterface;
      } else {
        result = vm.propertyViewModels.get(MetaDataUtils.toCamelCase(field.name)) as PropertyViewModelInterface;
      }

      if (result === null || result === undefined) {
        if (field.isExtension) {
          result = vm.extension.relationViewModels.get(MetaDataUtils.toCamelCase(field.name)) as ExternalViewModelInterface;
        } else {
          result = vm.relationViewModels.get(MetaDataUtils.toCamelCase(field.name)) as ExternalViewModelInterface;
        }
      }

      if (result === null || result === undefined) {
        this.propertyViewModelCache[cacheKey] = null;
        return null;
      }

      result.metadataShortDescription = field.customDescriptions?.displayName?.length > 0 ? field.customDescriptions?.displayName : field.descriptions?.displayName;
      result.metadataDescription = field.customDescriptions?.description?.length > 0 ? field.customDescriptions?.description : field.descriptions?.description;
    }

    this.propertyViewModelCache[cacheKey] = result;
    return result;
  }

  getPropertyViewModelFromFieldMetaData(field: FieldMetaDataInterface): PropertyViewModelInterface {
    return this.getPropertyFromFieldMetaData(field) as PropertyViewModelInterface;
  }

  getCollectionViewModelFromGridFieldMetaData(field: GridMetaDataInterface) {
    if (field.path?.length > 0) {
      return this.getResolvedPropertyPath(field)[field.name];
    } else {
      return this.orchestratorViewModel.rootViewModel[field.name];
    }

  }

  getCollectionViewModelColumnsFromGridFieldMetaData(field: GridMetaDataInterface): ColumnInfoCollectionInterface {
    const cacheKey = field.path?.length > 0 ? field.path + '.' + field.name : field.name;
    if (this.collectionViewModelColumnCache[cacheKey]) {
      return this.collectionViewModelColumnCache[cacheKey];
    }

    if (field.gridColumns?.sortedFields.length > 0) {

      // field.gridColumns.sortedFields.filter(f => f.isVisible).forEach((f) => {
      //     if (f?.path?.endsWith('selectedItem')) {
      //         return f.name;
      //     }
      //     const path = f?.path?.substring(f?.path?.lastIndexOf('.selectedItem.') + '.selectedItem.'.length);
      //     return path?.length > 0 ? path + '.' + f.name : f.name;
      // })

      const visibilityDefinition = field.gridColumns.sortedFields.filter((f) => f.isVisible === true).map(
        (f) => {
          if (f?.path?.endsWith('selectedItem')) {
            return f.name;
          }
          const path = f?.path?.substring(f?.path?.lastIndexOf('.selectedItem.') + '.selectedItem.'.length);
          return path?.length > 0 ? path + '.' + f.name : f.name;
        });

      const widthDefinition = field.gridColumns.sortedFields.filter((f) => f.isVisible === true).map(
        (f) => {

          let fullPropertyName = f.name;
          if (!f?.path?.endsWith('selectedItem')) {
            const path = f?.path?.substring(f?.path?.lastIndexOf('.selectedItem.') + '.selectedItem.'.length);
            fullPropertyName = path?.length > 0 ? path + '.' + f.name : f.name;
          }
          const isAutoSize = f.isAutoSize;
          const width = f.width;
          return {
            fullPropertyName, isAutoSize, width
          };
        });

      const autocompleteDefinition = field.gridColumns.externals.filter((f) => f.isVisible === true).map(
        (f) => {

          let fullPropertyName = f.name;
          if (!f?.path?.endsWith('selectedItem')) {
            const path = f?.path?.substring(f?.path?.lastIndexOf('.selectedItem.') + '.selectedItem.'.length);
            fullPropertyName = path?.length > 0 ? path + '.' + f.name : f.name;
          }
          return {
            fullPropertyName,
            decodeProperties: f.decodeProperties?.length > 0 ? f.decodeProperties : null,
            searchProperties: f.searchProperties?.length > 0 ? f.searchProperties : null,
            fieldSeparator: f.fieldSeparator,
            showCodeInDescription: false
          };

        }).filter((e) => e);

      const editableDefinition = field.areAllColumnsDisabled ? false : field.gridColumns.sortedFields.filter((f) => f.isDisabled === true).map((f) => {
        if (f?.path?.endsWith('selectedItem')) {
          return f.name;
        }
        const path = f?.path?.substring(f?.path?.lastIndexOf('.selectedItem.') + '.selectedItem.'.length);
        return path?.length > 0 ? path + '.' + f.name : f.name;
      });

      const column = this.rootViewModel.getGridColumns({
        fullPathName: field.path?.length > 0 ? field.path + '.' + field.name : field.name,
        entityTypeName: field.gridColumns.modelName,
        visibilityDefinition,
        editableDefinition,
        orderDefinition: visibilityDefinition,
        widthDefinition,
        autocompleteDefinition
      });

      this.collectionViewModelColumnCache[cacheKey] = column;
      this.fixColumnsWithDescriptionsFromLayout(column, field.gridColumns.sortedFields);
      return column;

    } else {
      if (field.path?.length > 0) {
        const resolved = this.getResolvedPropertyPath(field);
        if (resolved != null && resolved[field.name + 'Columns']) {
          return resolved[field.name + 'Columns'] as ColumnInfoCollectionInterface;
        } else {
          return this.orchestratorViewModel.rootViewModel[field.name + 'Columns'] as ColumnInfoCollectionInterface;
        }
      } else {
        return this.orchestratorViewModel.rootViewModel[field.name + 'Columns'] as ColumnInfoCollectionInterface;
      }
    }
  }

  fixColumnsWithDescriptionsFromLayout(columns: ColumnInfoCollectionInterface, gridFieldListFromLayout: GridFieldInterface[]) {
    for (const [index, column] of columns.entries()) {
      column.header = gridFieldListFromLayout.filter((f) => f.isVisible === true)[index].displayName;
      column.headerTooltip = gridFieldListFromLayout.filter((f) => f.isVisible === true)[index].description;
    }
  }

  getExternalLayoutMetaData(field: FieldMetaDataInterface): ExternalLayoutMetaDataInterface {
    return field as ExternalLayoutMetaDataInterface;
  }

  getExternalPropertyViewModelFromExternalMetaData(field: ExternalLayoutMetaDataInterface): ExternalViewModelInterface {
    return this.getPropertyFromFieldMetaData(field) as ExternalViewModelInterface;
  }

  getResolvedPropertyPath(field: FieldMetaDataInterface) {
    const correctedPath: string = this.fixSelectedItemInPath(field.path);
    return correctedPath.split('.').reduce((acc, current) => acc[current], this.orchestratorViewModel.rootViewModel);
  }

  /**
   * Corregge i path che hanno i selected item
   * @param path path da correggere
   * @returns path corretto
   */
  fixSelectedItemInPath(path: string): string {
    return path.replace(/selectedItem/g, 'selectedItemOrMocked');
  }
}
